import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { ComplaintCategory, RoleEnum } from '@zeus/index'
import { ImagePlus, Loader, X } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import FilePickerButton from '~/components/FilePickerButton'
import RequiredFieldsHint from '~/components/RequiredFieldsHint'
import { SidePanel } from '~/components/SidePanel'
import { Button } from '~/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { GQL_SEND_CONTACT_REQUEST } from '~/gql/contact/contact'
import { readFileAsDataUri } from '~/helpers/files/read-file-as-data-uri'
import { useAuth } from '~/helpers/store/auth'
import { useContactPanel } from '~/helpers/store/contact-panel'
import ContactCategoryField from '~/pages/help/components/ContactCategoryField'
import { contactErrorKey } from '~/pages/help/contact-errors'

/** Mêmes bornes que l'API (`contact.service.ts`, `image-attachment.ts`). */
const MAX_SUBJECT = 150
const MAX_MESSAGE = 5000
const MAX_ATTACHMENT_BYTES = 1024 * 1024
const IMAGE_TYPES = ['image/png', 'image/jpeg']

/**
 * Formulaire « Besoin d'aide » (#81), dans un tiroir ouvert depuis les menus ou
 * le centre d'aide. La page et la langue courantes partent avec la demande : le
 * destinataire sait d'où l'on écrit sans avoir à le demander.
 */
function ContactPanel() {
  const { t, i18n } = useTranslation()
  const { payload } = useAuth()
  const { isOpen, close } = useContactPanel()
  const [send, { loading }] = useMutation(GQL_SEND_CONTACT_REQUEST)
  const [attachment, setAttachment] = useState<{ name: string, dataUri: string } | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)
  // Seul l'apprenant classe sa demande : elle entre au registre de son organisme (#325).
  const learner = payload?.role === RoleEnum.User
  const [category, setCategory] = useState<ComplaintCategory>(ComplaintCategory.QUESTION)

  const schema = z.object({
    subject: z.string().trim().min(1, { message: t('contact.error.subjectRequired') }).max(MAX_SUBJECT, { message: t('contact.error.subjectTooLong') }),
    message: z.string().trim().min(1, { message: t('contact.error.messageRequired') }).max(MAX_MESSAGE, { message: t('contact.error.messageTooLong') }),
  })
  type Values = z.infer<typeof schema>
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { subject: '', message: '' } })

  const reset = () => {
    form.reset()
    setCategory(ComplaintCategory.QUESTION)
    setAttachment(null)
    setFileError(null)
    setApiError(null)
  }

  const onFile = async (file: File | undefined) => {
    setFileError(null)
    if (!file) return
    if (!IMAGE_TYPES.includes(file.type)) return setFileError(t('contact.attachment.invalidType'))
    if (file.size > MAX_ATTACHMENT_BYTES) return setFileError(t('contact.attachment.tooLarge'))
    setAttachment({ name: file.name, dataUri: await readFileAsDataUri(file) })
  }

  const onSubmit = async (values: Values) => {
    setApiError(null)
    try {
      await send({
        variables: {
          input: {
            ...values,
            pageUrl: window.location.href,
            locale: i18n.language,
            attachment: attachment?.dataUri ?? null,
            ...(learner && { category }),
          },
        },
      })
      toast.success(t('contact.sent'))
      reset()
      close()
    } catch (error) {
      setApiError(t(contactErrorKey(error)))
    }
  }

  return (
    <SidePanel
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          reset()
          close()
        }
      }}
      title={t('contact.title')}
      description={learner ? t('contact.recipient.admins') : t('contact.recipient.support')}
      footer={(
        <>
          <Button variant="outline" type="button" onClick={close}>{t('common.cancel')}</Button>
          <Button type="submit" form="contact-form" disabled={loading}>
            {loading && <Loader className="mr-2 size-4 animate-spin" />}
            {t('contact.send')}
          </Button>
        </>
      )}
    >
      <Form {...form}>
        <form id="contact-form" onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <RequiredFieldsHint />
          {learner && <ContactCategoryField value={category} onChange={setCategory} />}
          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t('contact.subject')}</FormLabel>
                <FormControl><Input aria-required {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>{t('contact.message')}</FormLabel>
                <FormControl>
                  <textarea aria-required rows={8} className="block w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-2">
            <p id="contact-attachment-label" className="text-sm font-medium">{t('contact.attachment.label')}</p>
            {attachment
              ? (
                  <div className="flex items-center gap-3">
                    <img src={attachment.dataUri} alt="" className="h-16 w-auto rounded border border-border" />
                    <span className="min-w-0 truncate text-sm">{attachment.name}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setAttachment(null)} aria-label={t('contact.attachment.remove')}>
                      <X className="size-4" />
                    </Button>
                  </div>
                )
              : (
                  <FilePickerButton accept={IMAGE_TYPES.join(',')} onFile={onFile} className="self-start" aria-describedby="contact-attachment-label">
                    <ImagePlus className="mr-2 size-4" />
                    {t('contact.attachment.choose')}
                  </FilePickerButton>
                )}
            <p className="text-xs text-muted-foreground">{t('contact.attachment.hint')}</p>
            {fileError && <p role="alert" className="text-sm text-destructive">{fileError}</p>}
          </div>

          {apiError && <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{apiError}</p>}
        </form>
      </Form>
    </SidePanel>
  )
}

export default ContactPanel
